import { ReactNode } from "react";

type PaginationProps = {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  summary?: ReactNode;
};

/**
 * Builds the list of page numbers to show, collapsing long ranges with
 * ellipsis markers around the first, last, and current pages.
 */
function getPageItems(current: number, total: number): (number | "ellipsis")[] {
  const pages = new Set<number>([1, total]);
  for (let candidate = current - 1; candidate <= current + 1; candidate += 1) {
    if (candidate >= 1 && candidate <= total) {
      pages.add(candidate);
    }
  }

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const items: (number | "ellipsis")[] = [];
  let previous = 0;

  for (const value of sorted) {
    if (value - previous > 1) {
      items.push("ellipsis");
    }
    items.push(value);
    previous = value;
  }

  return items;
}

function Pagination({
  page,
  totalPages,
  onPageChange,
  disabled = false,
  summary,
}: PaginationProps) {
  const lastPage = totalPages > 0 ? totalPages : 1;

  if (lastPage <= 1) {
    return summary ? (
      <div className="pagination">
        <p className="hintText">{summary}</p>
      </div>
    ) : null;
  }

  const items = getPageItems(page, lastPage);

  return (
    <nav className="pagination" aria-label="Pagination">
      {summary ? <p className="hintText">{summary}</p> : null}

      <ul className="paginationPages">
        {items.map((item, index) =>
          item === "ellipsis" ? (
            <li
              key={`ellipsis-${index}`}
              className="paginationEllipsis"
              aria-hidden="true"
            >
              …
            </li>
          ) : (
            <li key={item}>
              <button
                type="button"
                className={item === page ? "pageButton active" : "pageButton"}
                aria-current={item === page ? "page" : undefined}
                aria-label={`Page ${item}`}
                onClick={() => onPageChange(item)}
                disabled={disabled}
              >
                {item}
              </button>
            </li>
          ),
        )}
      </ul>
    </nav>
  );
}

export default Pagination;
